import { skipToken } from '@tanstack/react-query'
import { useMemo } from 'react'

import { APPOINTMENT_TYPES_BY_ID, type ClinicId, getClinicTimezone } from '@snootsvet/shared'

import {
  type CalendarViewType,
  filterFullDayScheduleBlocks,
  getMergedScheduleBackgroundEvents,
  getScheduleBackgroundEvents,
  toApiDateRange,
  toBlockCalendarEvent,
  toCalendarEvent,
} from '@/frontend/features/appointments/appointment-calendar.helpers'
import type { CalendarAvailability } from '@/frontend/features/appointments/appointment-calendar-provider'
import { getDisplayStatus } from '@/frontend/features/appointments/appointments.utils'
import { useClinicSchedule } from '@/frontend/features/appointments/use-clinic-schedule'
import { useListAppointments } from '@/frontend/features/appointments/use-list-appointments'
import { useListBlockedSlots } from '@/frontend/features/appointments/use-list-blocked-slots'
import { useAvailableSlots } from '@/frontend/hooks/api/use-available-slots'
import { useListStaff } from '@/frontend/hooks/api/use-list-staff'

interface UseAppointmentCalendarDataOpts {
  clinicId: ClinicId
  viewType: CalendarViewType
  dateRange: { from: Date; to: Date } | null
  staffIdsFilter: string[] | undefined
  statuses: string[]
  availability: CalendarAvailability | null
}

/**
 * Loads appointments, blocked time, schedules, staff, and availability for the visible clinic range.
 * Builds filtered FullCalendar events and staff resources from the fetched data.
 * Exposes appointment fetching status and availability loading, error, empty, and retry state.
 */
export function useAppointmentCalendarData({
  clinicId,
  viewType,
  dateRange,
  staffIdsFilter,
  statuses,
  availability,
}: UseAppointmentCalendarDataOpts) {
  const timeZone = getClinicTimezone(clinicId)
  const clinicDateRange = useMemo(
    () => (dateRange ? toApiDateRange(dateRange.from, dateRange.to, timeZone) : null),
    [dateRange, timeZone]
  )

  const {
    data: appointments = [],
    error,
    isFetching,
  } = useListAppointments({
    ...clinicDateRange,
    clinicId,
    staffIds: staffIdsFilter,
    refetchInterval: 10_000,
  })

  const { data: blocks = [] } = useListBlockedSlots({
    ...clinicDateRange,
    staffIds: staffIdsFilter,
    clinicId,
    refetchInterval: 10_000,
  })

  const { data: staff } = useListStaff()

  const { data: scheduleDays = [] } = useClinicSchedule({
    clinicId,
    from: clinicDateRange?.from,
    to: clinicDateRange?.to,
  })

  const singleStaffId = staffIdsFilter?.length === 1 ? staffIdsFilter[0] : undefined

  const availabilityParams =
    availability && clinicDateRange
      ? {
          clinicId,
          from: clinicDateRange.from,
          to: clinicDateRange.to,
          staffId: singleStaffId,
          ...(availability.type === 'rescheduling'
            ? { appointmentId: availability.appointment.id }
            : { appointmentTypeId: availability.appointment.appointmentTypeId }),
        }
      : skipToken

  const {
    data: slots = [],
    isLoading: slotsLoading,
    isError: slotsError,
    refetch: refetchSlots,
  } = useAvailableSlots(availabilityParams)

  const availabilityEvents = useMemo(() => {
    if (!availability || !slots.length) return []
    const type = APPOINTMENT_TYPES_BY_ID[availability.appointment.appointmentTypeId]
    return slots.flatMap(({ start, end, staffIds }) => {
      const baseProps = {
        start,
        end,
        title: 'Available',
        backgroundColor: '#4ade80',
        borderColor: '#16a34a',
        textColor: '#14532d',
        classNames: ['fc-availability-slot'],
        editable: false,
        extendedProps: {
          isAvailabilitySlot: true,
          appointmentTypeId: availability.appointment.appointmentTypeId,
          availabilityLabel:
            availability.type === 'rescheduling'
              ? `${type.name} - ${availability.appointment.patient.name}`
              : type.name,
        },
      }
      if (viewType === 'day' && staffIds.length > 0) {
        return staffIds.map((staffId) => ({
          ...baseProps,
          id: `avail-${start}-${staffId}`,
          resourceId: staffId,
          extendedProps: { ...baseProps.extendedProps, staffId },
        }))
      }
      return [
        {
          ...baseProps,
          id: `avail-${start}`,
          extendedProps: { ...baseProps.extendedProps, staffId: staffIds[0] },
          ...(staffIds.length === 1 ? { resourceId: staffIds[0] } : {}),
        },
      ]
    })
  }, [slots, availability, viewType])

  // Week view temporarily removes full-day blocked-slot records and the schedule coverage they replace.
  // Remove this branch once staff use schedule overrides for full days off instead of blocked slots.
  const calendarData = useMemo(
    () =>
      viewType === 'week'
        ? filterFullDayScheduleBlocks(scheduleDays, blocks, timeZone)
        : { scheduleDays, blocks },
    [scheduleDays, blocks, timeZone, viewType]
  )

  const scheduleEvents = useMemo(() => {
    if (!calendarData.scheduleDays.length || !staff?.length) return []

    if (viewType === 'week') {
      const staffEmails = staff
        .filter((s) => !staffIdsFilter?.length || staffIdsFilter.includes(s.id))
        .map((s) => s.email)
      // Week shading represents combined coverage after full-day blocked slots are removed.
      return getMergedScheduleBackgroundEvents(calendarData.scheduleDays, staffEmails, timeZone)
    }

    if (staffIdsFilter?.length === 1) {
      const s = staff.find((s) => s.id === staffIdsFilter[0])
      return s ? getScheduleBackgroundEvents(calendarData.scheduleDays, s.email, timeZone) : []
    }

    if (viewType === 'day') {
      return staff.flatMap((s) =>
        getScheduleBackgroundEvents(calendarData.scheduleDays, s.email, timeZone, s.id)
      )
    }

    return []
  }, [calendarData, staff, staffIdsFilter, timeZone, viewType])

  const resources = useMemo(() => {
    if (!staff) return []

    if (staffIdsFilter?.length) {
      return staff
        .filter((s) => staffIdsFilter.includes(s.id))
        .map((s) => ({ id: s.id, title: `${s.firstName} ${s.lastName}` }))
    }

    if (viewType === 'day' && dateRange) {
      const workingEmails = scheduleDays.flatMap((d) => d.vets.map((v) => v.staff.email))
      const visibleAppointments =
        statuses.length > 0
          ? appointments.filter((a) => statuses.includes(getDisplayStatus(a)))
          : appointments
      const appointmentStaffIds = new Set(visibleAppointments.map((a) => a.staffId))

      const active = staff
        .filter((s) => workingEmails.includes(s.email) || appointmentStaffIds.has(s.id))
        .map((s) => ({ id: s.id, title: `${s.firstName} ${s.lastName}` }))

      // Soft-deleted vets are absent from the staff list, but their appointments
      // still need a column or the resource view silently drops them.
      const activeIds = new Set(staff.map((s) => s.id))
      const former = new Map(
        visibleAppointments
          .filter((a) => !activeIds.has(a.staffId))
          .map((a) => [
            a.staffId,
            { id: a.staffId, title: `${a.staff.firstName} ${a.staff.lastName} (former)` },
          ])
      )

      return [...active, ...former.values()]
    }

    return staff.map((s) => ({ id: s.id, title: `${s.firstName} ${s.lastName}` }))
  }, [staff, staffIdsFilter, viewType, dateRange, scheduleDays, appointments, statuses])

  const calendarEvents = useMemo(() => {
    const filtered =
      statuses.length > 0
        ? appointments.filter((a) => statuses.includes(getDisplayStatus(a)))
        : appointments

    return [
      ...filtered.map(toCalendarEvent),
      ...calendarData.blocks.map(toBlockCalendarEvent),
      ...scheduleEvents,
      ...availabilityEvents,
    ]
  }, [appointments, statuses, calendarData, scheduleEvents, availabilityEvents])

  return {
    calendarEvents,
    resources,
    error,
    isFetching,
    slotsLoading,
    slotsError,
    slotsEmpty: slots.length === 0,
    refetchSlots,
  }
}
